<template>
  <span class="inline-action-buttons">
    <img class="img" v-if="img" :src="img"/>
    <a :title="!expanded ? $t('edit') : ''"
       v-if="hasRightToEditPlan"
       @click="editPlanTreatment"
       v-b-tooltip.hover
       class="data-table-action">
    <EditSvg class="svg-stroke-light"/><span class="action-button-mob-title">{{ $t('edit') }}</span>
  </a>
    <a :title="!expanded ? $t('duplicate') : ''"
       v-if="hasRightToEditPlan"
       @click="copyPlanTreatment"
       v-b-tooltip.hover
       class="data-table-action">
    <CopySvg/><span class="action-button-mob-title">{{ $t('duplicate') }}</span>
  </a>
    <a :title="!expanded ? $t('refresh_prices_and_materials') : ''"
       v-if="canEditPlan && rights.includes('refresh_plan_treatment_prices_button')"
       @click="updatePlanTreatmentPrices"
       v-b-tooltip.hover
       class="data-table-action">
    <RefreshSvg/><span class="action-button-mob-title">{{ $t('refresh_prices_and_materials') }}</span>
  </a>
    <a :title="!expanded ? $t('sign') : ''"
       v-if="!object.signed_at && canSign"
       @click="sign"
       v-b-tooltip.hover
       class="data-table-action">
    <SignSvg class="svg-light svg-size-xl"/><span class="action-button-mob-title">{{ $t('sign') }}</span>
  </a>
    <a class="data-table-action"
       v-b-tooltip.hover
       :title="!expanded ? $t('print') : ''">
      <b-dropdown dropleft variant="link" no-caret>
        <template #button-content>
          <PrintSvg/><span class="action-button-mob-title">{{ $t('print') }}</span>
        </template>
        <a class="dropdown-item pointer" @click="printPlan()">
          {{ $t('standard') }}
        </a>
        <a class="dropdown-item pointer" @click="printPlanGrouped()">
          {{ $t('grouped') }}
        </a>
        <a class="dropdown-item pointer" @click="printPlanGroupedByTeeth()">
          {{ $t('grouped_by_teeth') }}
        </a>
        <a class="dropdown-item pointer" @click="printPlanGroupedByServices()">
          {{ $t('grouped_by_services') }}
        </a>
        <a class="dropdown-item pointer" @click="printPlanFact()">
          {{ $t('plan_fact') }}
        </a>
         <a class="dropdown-item pointer" @click="printRouteList()">
          {{ $t('route_list') }}
        </a>
        <div class="dropdown-divider"></div>
        <div class="dropdown-header font-weight-bold">{{  $t('with_formula') }}</div>
        <a class="dropdown-item pointer" @click="printWithFormula()">
          {{ $t('standard') }}
        </a>
        <a class="dropdown-item pointer" @click="printWithFormulaByTeeth()">
          {{ $t('grouped_by_teeth') }}
        </a>
        <a class="dropdown-item pointer" @click="printWithFormulaByServices()">
          {{ $t('grouped_by_services') }}
        </a>
        <div class="dropdown-divider"></div>
        <div class="dropdown-header font-weight-bold">{{  $t('by_stages') }}</div>
        <a class="dropdown-item pointer"
           v-for="(complex, index) in object.visits"
           :key="`visit-${complex.id}`"
           @click="printPlanComplex(complex)">
          {{ complex.title ? complex.title : $t('stage')+' '+(index+1) }}
        </a>
      </b-dropdown>
    </a>
    <a class="data-table-action"
       v-for="messenger in messengerOptions"
       :key="`messenger-${messenger}`"
       v-b-tooltip.hover
       :title="!expanded ? $t('send_to_patient_via_'+messenger) : ''">
      <b-dropdown right variant="link" no-caret>
        <template #button-content>
          <component :is="`${capitalize(messenger)}Svg`" class="svg-light svg-size-lg"/><span class="action-button-mob-title">{{ $t('send_to_patient_via_'+messenger) }}</span>
        </template>
        <a class="dropdown-item pointer" @click="printPlan(true, messenger)">
          {{ $t('standard') }}
        </a>
        <a class="dropdown-item pointer" @click="printPlanGrouped(true, messenger)">
          {{ $t('grouped') }}
        </a>
        <a class="dropdown-item pointer" @click="printPlanGroupedByTeeth(true, messenger)">
          {{ $t('grouped_by_teeth') }}
        </a>
        <a class="dropdown-item pointer" @click="printPlanGroupedByServices(true, messenger)">
          {{ $t('grouped_by_services') }}
        </a>
        <a class="dropdown-item pointer" @click="printPlanFact(true, messenger)">
          {{ $t('plan_fact') }}
        </a>
        <a class="dropdown-item pointer" @click="printWithFormula(true, messenger)">
          {{ $t('with_formula') }}
        </a>
         <a class="dropdown-item pointer" @click="printRouteList(true, messenger)">
          {{ $t('route_list') }}
        </a>
        <div class="dropdown-divider"></div>
        <div class="dropdown-header font-weight-bold">{{  $t('by_stages') }}</div>
        <a class="dropdown-item pointer"
           v-for="(complex, index) in object.visits"
           :key="`visit-${complex.id}`"
           @click="printPlanComplex(complex, true, messenger)">
          {{ complex.title ? complex.title : $t('stage')+' '+(index+1) }}
        </a>
      </b-dropdown>
    </a>
    <PlanSignModal v-if="showModal"
                   :plan="object"
                   @hidden="showModal = false"/>
    <OptionsPopup v-if="optionsModalOpen"
                  :title="$t('choose_signer')"
                  @changed="modalResolve"
                  :options="signerOptions"/>
  </span>
</template>

<script>
import EditSvg from '@/assets/svg-vue/general/edit2.svg'
import PrintSvg from '@/assets/svg-vue/general/print2.svg'
import CopySvg from '@/assets/svg-vue/patients/copy2.svg'
import SignSvg from '@/assets/svg-vue/patients/sign.svg'
import RefreshSvg from '@/assets/svg-vue/general/refresh2.svg'
import WhatsappSvg from '@/assets/svg-vue/crm/whatsapp.svg'
import TelegramSvg from '@/assets/svg-vue/calendar/menu_sms.svg'
import MaxSvg from '@/assets/svg-vue/crm/max.svg'
import JwtService from "@/services/jwt.service"
import {mapGetters, mapState} from "vuex"
import ApiService from "@/services/api.service"
import { PatientService } from "@/services/api/patient.api.service"
import { CalendarService } from "@/services/api/calendar.api.service"
import PlanSignModal from "@/components/parts/planning/PlanSignModal.vue"
import OptionsPopup from "@/components/parts/general/OptionsPopup.vue"
import {capitalize} from "vue"
import {canEditPlanTreatment} from "@/services/plan.helper"

export default {
  name: "PatientDatatablePlanTreatmentActions",
  components: {
    OptionsPopup,
    PlanSignModal,
    EditSvg,
    PrintSvg,
    CopySvg,
    SignSvg,
    WhatsappSvg,
    TelegramSvg,
    MaxSvg,
    RefreshSvg
  },
  props: {
    object: {
      type: Object
    },
    expanded: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      clinic: state => state.auth.clinic,
      branch: state => state.auth.branch,
      patient: state => state.patient.patient,
      rights: state => state.auth.rights,
    }),
    ...mapGetters(['canSendViaWhatsApp', 'maxEnabled']),
    hasRepresentative() {
      return this.patient.representative_id || this.patient.representative_fio
    },
    signerOptions() {
      return [
        { id: 'patient', title: this.$t('patient') },
        { id: 'representative', title: this.$t('representative_noun') },
      ]
    },
    messengerOptions() {
      const arr = []
      if(this.canSendViaWhatsApp) {
        arr.push('whatsapp')
        arr.push('telegram')
      }
      if(this.maxEnabled) {
        arr.push('max')
      }
      return arr
    },
    hasRightToEditPlan() {
      return this.rights.includes('edit_plan_treatment')
          || this.rights.includes('edit_plan_treatment_limited')
    },
    canEditPlan() {
      return canEditPlanTreatment(this.object, this.rights)
    },
    canSign() {
      return this.rights.includes('sign_plan_treatments')
    }
  },
  data() {
    return {
      img: null,
      showModal: false,
      optionsModalOpen: false,
      modalResolve: () => {}
    }
  },
  methods: {
    capitalize,
    editPlanTreatment() {
      this.$router.push({
        name: 'TreatmentPlanFullCard',
        params: {
          patientId: this.object.client_id,
          planId: this.object.planning_id,
        }
      })
    },
    copyPlanTreatment() {
      CalendarService.duplicatePlanTreatment(this.object.planning_id).then(() => {
        this.$noty.info(this.$t('success_message'))
        this.$store.commit('incrementDataTableCounter')
      }).catch((e) => { console.log(e) })

    },
    updatePlanTreatmentPrices() {
      CalendarService.updatePlanTreatmentPrices(this.object.planning_id).then(() => {
        this.$noty.info(this.$t('success_message'))
      }).catch((e) => { console.log(e) })
    },
    async printGenericFunction(query, getRepresentative = true, send = false, messenger = 'whatsapp') {
      let patientSigner = ''
      if(getRepresentative && this.hasRepresentative) {
        patientSigner = await this.openOptionsModal()
        if(!patientSigner) return
      }
      let link = JwtService.getPrintPrelink(this.clinic)
      let url = `${link}/plan/${this.object.planning_id}?${query}&patientSigner=${patientSigner}&branchId=${this.branch}`
      if(!send) {
        window.open(url, '_blank')
      }
      if(send) {
        try {
          const res = await ApiService.get(`${url}&sendVia${capitalize(messenger)}=1`)
          const success = res?.data === 0
          if(success) {
            this.$noty.info(this.$t('sent'))
          } else {
            this.$noty.error(this.$t('error_message'))
          }
        } catch (e) {
          console.log(e)
        }
      }
    },
    async printPlan(send = false, messenger = 'whatsapp') {
      await this.printGenericFunction(`p=1`, true, send, messenger)
    },
    async printPlanComplex(complex, send = false, messenger = 'whatsapp') {
      await this.printGenericFunction(`complexId=${complex.id}`, true, send, messenger)
    },
    async printPlanGrouped(send = false, messenger = 'whatsapp') {
      await this.printGenericFunction(`grouped=1`, true, send, messenger)
    },
    async printPlanGroupedByTeeth(send = false, messenger = 'whatsapp') {
      await this.printGenericFunction(`grouped=1&by_teeth=1`, true, send, messenger)
    },
    async printPlanGroupedByServices(send = false, messenger = 'whatsapp') {
      await this.printGenericFunction(`grouped=1&by_services=1`, true, send, messenger)
    },
    async printWithFormulaByTeeth() {
      await this.printWithFormula(false, '', 'by_teeth')
    },
    async printWithFormulaByServices() {
      await this.printWithFormula(false, '', 'by_services')
    },
    async printPlanFact(send = false, messenger = 'whatsapp') {
      await this.printGenericFunction(`planfact=1`, true, send, messenger)
    },
    async printRouteList(send = false, messenger = 'whatsapp') {
      await this.printGenericFunction(`route_list=1`, false, send, messenger)
    },
    async openOptionsModal() {
      this.optionsModalOpen = true
      await this.$nextTick()
      this.$bvModal.show('options-popup-modal')
      return new Promise((resolve) => {
        this.modalResolve = resolve
      });
    },
    async sign() {
      this.showModal = true
      await this.$nextTick()
      this.$bvModal.show('plan-sign-modal')
    },
    async printWithFormula(send = false, messenger = 'whatsapp', type = 'standard') {
      try {
        this.$store.dispatch('decideWhichPatientFormulaViewToShow', {
          plan: this.object
        })
        await this.$nextTick()
        const el = document.getElementById('patientFormulaSelectedView')
        const rect = el.getBoundingClientRect()

        console.log({
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
          scrollX: window.scrollX,
          scrollY: window.scrollY,
          dpr: window.devicePixelRatio,
          zoom: window.visualViewport?.scale
        });

        const options = {
          type: 'dataURL',
          allowTaint: true,
          scale: window.devicePixelRatio,
          scrollX: -window.scrollX,
          scrollY: -window.scrollY,
          onclone: function(doc) {
            const hiddenDiv = doc.getElementById('patientFormula')
            hiddenDiv.style.display = 'block'
          }
        }
        const style = document.createElement('style') // this style addition is a hack to keep the styling, otherwise some paddings appear and image is cropped
        document.head.appendChild(style)
        style.sheet?.insertRule('body > div:last-child img { display: inline-block; }')
        await this.$nextTick()
        try {
          const output = await this.$html2canvas(el, options)
          style.remove()
          const res = await PatientService.sendFormulaImage(this.object.client_id, output)
          if(res.data) {
            let additionalUrl = ''
            if(type === 'by_teeth') additionalUrl = '&grouped=1&by_teeth=1'
            if(type === 'by_services') additionalUrl = '&grouped=1&by_services=1'
            await this.printGenericFunction(`formula=1&by_services=1${additionalUrl}`, true, send, messenger)
          }
        } catch (e) {
          console.log(e)
        }
      } catch (e) {
        console.log(e)
      }
    },
  }
}
</script>

<style scoped lang="scss">
.inline-action-buttons {
  display: contents;
}
</style>
